import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useLayoutEffect } from 'react';
import { notification } from 'antd';
import { getErrorMessage } from '../../helpers/errorHandler';
import { v4 as uuidv4 } from 'uuid';
import { useNavigate } from 'react-router-dom';
import { useMainContext } from '../../store/MainContext';
import React from 'react';
import { resetMainState } from '../config/initial-main-state';
import { deleteUserInfo } from '../../helpers/localStorageHandler';

// Add a request interceptor
function AxiosWrapper({ children }: { children: React.ReactNode }) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { setMainState } = useMainContext();
	useLayoutEffect(() => {
		const requestInterceptor = axios.interceptors.request.use(
			(config) => {
				const traceId = uuidv4();
				config.withCredentials = true;
				config.headers['portal'] = 'admin';
				// Ensure JWT auth headers are never sent from the client.
				delete config.headers.Authorization;
				if (!config?.url?.includes('configuration/public')) {
					// Set the "X-TraceId" header in the request config
					config.headers['x-TraceId'] = traceId;
				}
				return config;
			},
			(error) => {
				return Promise.reject(error);
			},
		);

		const responseInterceptor = axios.interceptors.response.use(
			(response) => response,
			async (error) => {
				const requestUrl = error?.config?.url || '';
				const errorCode = Number(error?.response?.data.code);
				const data = error?.response?.data?.extraAttributes ?? '';
				const errorMessage = getErrorMessage(errorCode, t, data);
				if (errorMessage && error.response.status !== 401) {
					notification['error']({ message: '', description: errorMessage, placement: 'bottom' });
				}
				if (error.response && error.response.status === 401 && requestUrl.includes('login')) {
					notification['error']({ message: '', description: errorMessage, placement: 'bottom' });
				}
				if (error.response && error.response.status === 401 && !requestUrl.includes('login')) {
					setMainState(resetMainState);
					deleteUserInfo();
					navigate(`/sign-in`);
					return new Promise(() => {});
				}

				return Promise.reject(error);
			},
		);
		return () => {
			axios.interceptors.request.eject(requestInterceptor);
			axios.interceptors.response.eject(responseInterceptor);
		};
	}, [navigate, setMainState, t]);
	return <>{children}</>;
}

export const MemoizedAxiosWrapper = React.memo(AxiosWrapper);
